import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiMenu, FiSearch, FiX, FiRefreshCw, FiBell, FiMessageSquare, FiInbox } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import SearchAdmin from '../../Components/SearchAdmin/SearchAdmin';
import Logo from '../../../section/Header/Logo';
import { playNotificationChime, unlockAudio } from '../../../utils/notificationSound';
import './HeaderAdmin.css';

const COMMANDES_POLL_MS = 20000;

const API = process.env.REACT_APP_API_URL;

const formatRelative = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((new Date() - new Date(iso)) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `${diff} min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const HeaderAdmin = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const [connectedUser, setConnectedUser] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  const [commandes, setCommandes] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const msgRef = useRef(null);

  const authorization = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authorization}` };
  const knownCommandeIds = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const raw = localStorage.getItem('admin_data');
    if (raw) setConnectedUser(JSON.parse(raw));
  }, []);

  // Débloque le son au premier clic/tap — les navigateurs (surtout mobile)
  // empêchent l'audio de jouer avant une interaction utilisateur.
  useEffect(() => {
    const unlock = () => unlockAudio();
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const fetchCommandes = () => {
    axios.get(`${API}/commande/lister`, { headers }).then((res) => {
      const list = res.data;
      const ids = new Set(list.map((c) => c._id));

      if (knownCommandeIds.current === null) {
        // Premier chargement : on mémorise l'état sans notifier.
        knownCommandeIds.current = ids;
      } else {
        const nouvelles = list.filter((c) => !knownCommandeIds.current.has(c._id));
        if (nouvelles.length > 0) {
          playNotificationChime();
          nouvelles.forEach((c) => {
            toast(`🛒 Nouvelle commande de ${c.prenom} ${c.nom}`, { duration: 6000 });
          });
        }
        knownCommandeIds.current = ids;
      }

      setCommandes(list);
    }).catch(() => {});
  };
  const fetchMessages = () => {
    axios.get(`${API}/contact/lister`, { headers }).then((res) => setMessages(res.data)).catch(() => {});
  };

  useEffect(() => {
    fetchCommandes();
    fetchMessages();
    const interval = setInterval(fetchCommandes, COMMANDES_POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target)) setMsgOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pendingCommandes = commandes.filter((c) => (c.statut || 'En attente') === 'En attente');
  const unreadMessages = messages.filter((m) => !m.lu);

  const markMessageRead = async (id) => {
    try {
      await axios.post(`${API}/contact/${id}/lu`, { lu: true }, { headers });
      setMessages((prev) => prev.map((m) => (m._id === id ? { ...m, lu: true } : m)));
    } catch (err) { /* silent */ }
  };

  const initials = connectedUser
    ? `${connectedUser.prenom?.[0] || ''}${connectedUser.nom?.[0] || ''}`.toUpperCase()
    : 'AD';

  return (
    <header className="hd-admin-navbar">
      <Toaster position="top-right" />
      <div className="hd-admin-navbar-container">
      <Link to="/admin" className="hd-admin-logo-zone">
        <Logo />
      </Link>

      <div className="hd-admin-menu-toggle-zone">
        <button
          type="button"
          className="hd-admin-menu-toggle"
          onClick={onToggleSidebar}
          aria-label="Ouvrir le menu"
        >
          <FiMenu />
        </button>
      </div>

      <div className="hd-admin-navbar-search">
        <SearchAdmin />
      </div>
      <button
        type="button"
        className="hd-admin-search-mobile-btn"
        onClick={() => setShowSearch((s) => !s)}
        aria-label="Rechercher"
      >
        {showSearch ? <FiX /> : <FiSearch />}
      </button>

      <div className="hd-admin-spacer" />

      <div className="hd-admin-navbar-actions">
        <div className="hd-admin-status">
          <span className="hd-admin-status-dot" />
          <span className="hd-admin-status-text">En ligne</span>
        </div>

        <button className="hd-admin-icon-btn" title="Rafraîchir" onClick={() => window.location.reload()}>
          <FiRefreshCw size={15} />
        </button>

        <div className="hd-admin-panel-wrap" ref={notifRef}>
          <button
            type="button"
            className={`hd-admin-icon-btn ${notifOpen ? 'is-active' : ''}`}
            title="Notifications"
            onClick={() => { setNotifOpen((o) => !o); setMsgOpen(false); }}
          >
            <FiBell size={15} />
            {pendingCommandes.length > 0 && <span className="hd-admin-badge-num">{pendingCommandes.length}</span>}
          </button>

          {notifOpen && (
            <div className="hd-admin-dropdown">
              <div className="hd-admin-dropdown-header">
                <span>Notifications{pendingCommandes.length > 0 && <span className="hd-admin-dropdown-unread">{pendingCommandes.length}</span>}</span>
                <button className="hd-admin-dropdown-refresh" onClick={fetchCommandes}><FiRefreshCw size={12} /></button>
              </div>
              <div className="hd-admin-dropdown-list">
                {pendingCommandes.length === 0 ? (
                  <div className="hd-admin-dropdown-empty">
                    <FiBell size={28} />
                    <p>Aucune commande en attente</p>
                  </div>
                ) : (
                  pendingCommandes.slice(0, 8).map((c) => (
                    <div key={c._id} className="hd-admin-dropdown-item" onClick={() => { setNotifOpen(false); navigate('/admin/Commande'); }}>
                      <div className="hd-admin-dropdown-avatar is-order">🛒</div>
                      <div className="hd-admin-dropdown-content">
                        <div className="hd-admin-dropdown-top">
                          <span className="hd-admin-dropdown-name">Commande #{c._id?.slice(-6)}</span>
                          <span className="hd-admin-dropdown-date">{formatRelative(c.createdAt)}</span>
                        </div>
                        <p className="hd-admin-dropdown-text">{c.prenom} {c.nom}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {pendingCommandes.length > 0 && (
                <div className="hd-admin-dropdown-footer">
                  <button onClick={() => { setNotifOpen(false); navigate('/admin/Commande'); }}>
                    Voir tout ({pendingCommandes.length}) →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hd-admin-panel-wrap" ref={msgRef}>
          <button
            type="button"
            className={`hd-admin-icon-btn ${msgOpen ? 'is-active' : ''}`}
            title="Messages"
            onClick={() => { setMsgOpen((o) => !o); setNotifOpen(false); }}
          >
            <FiMessageSquare size={15} />
            {unreadMessages.length > 0 && <span className="hd-admin-badge-num">{unreadMessages.length}</span>}
          </button>

          {msgOpen && (
            <div className="hd-admin-dropdown">
              <div className="hd-admin-dropdown-header">
                <span>Messages{unreadMessages.length > 0 && <span className="hd-admin-dropdown-unread">{unreadMessages.length} non lu{unreadMessages.length > 1 ? 's' : ''}</span>}</span>
                <button className="hd-admin-dropdown-refresh" onClick={fetchMessages}><FiRefreshCw size={12} /></button>
              </div>
              <div className="hd-admin-dropdown-list">
                {messages.length === 0 ? (
                  <div className="hd-admin-dropdown-empty">
                    <FiInbox size={28} />
                    <p>Aucun message</p>
                  </div>
                ) : (
                  messages.slice(0, 6).map((m) => (
                    <div
                      key={m._id}
                      className={`hd-admin-dropdown-item ${!m.lu ? 'is-unread' : ''}`}
                      onClick={() => { markMessageRead(m._id); setMsgOpen(false); navigate('/admin/Reclamation'); }}
                    >
                      <div className={`hd-admin-dropdown-avatar ${!m.lu ? 'is-unread' : ''}`}>{m.email?.[0]?.toUpperCase() || '?'}</div>
                      <div className="hd-admin-dropdown-content">
                        <div className="hd-admin-dropdown-top">
                          <span className="hd-admin-dropdown-name">{m.sujet}</span>
                          <span className="hd-admin-dropdown-date">{formatRelative(m.createdAt)}</span>
                        </div>
                        <p className="hd-admin-dropdown-text">{m.message?.slice(0, 60)}{m.message?.length > 60 ? '…' : ''}</p>
                      </div>
                      {!m.lu && <span className="hd-admin-dropdown-dot" />}
                    </div>
                  ))
                )}
              </div>
              {messages.length > 0 && (
                <div className="hd-admin-dropdown-footer">
                  <button onClick={() => { setMsgOpen(false); navigate('/admin/Reclamation'); }}>
                    Voir tous les messages ({messages.length}) →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hd-admin-user">
          <div className="hd-admin-user-avatar">{initials}</div>
          <div className="hd-admin-user-details">
            <span className="hd-admin-user-name">
              {connectedUser ? `${connectedUser.prenom} ${connectedUser.nom}` : 'Administrateur'}
            </span>
            <span className="hd-admin-user-role">{connectedUser?.superAdmin ? 'Super Admin' : 'Administrateur'}</span>
          </div>
        </div>
      </div>
      </div>

      {showSearch && (
        <div className="hd-admin-search-mobile-panel">
          <SearchAdmin />
        </div>
      )}
    </header>
  );
};

export default HeaderAdmin;
